import type { RouteLocationNormalized, RouteLocationRaw, Router } from 'vue-router';
import type { AdminRole, AdminMe, AuctionEventStatus } from '@neon-one/auctions-types';

import { useAdminSession } from '@/shell/stores/adminSession';
import { useCurrentAuction } from '@/shell/stores/currentAuction';
import { landingRouteNameFor } from '@/shell/lifecycleLanding';
import { isJwtExpired } from '@/shell/auth/loadAdminUser';
import { httpClient } from '@/shell/http';
import { getOrgSlug, bareAdminUrl, orgAdminUrl, isOrgSlug } from '@/shell/org';
import { enterOrg, ORG_NO_ACCESS_PATH } from '@/shell/auth/orgSelection';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAdminAuth?: boolean;
    requiresSupervisor?: boolean;
    requiredRole?: AdminRole | 'any';
    /** Lifecycle gate: minimum auction status required to render this route. */
    lifecycle?: AuctionEventStatus | readonly AuctionEventStatus[];
    /** Skip current-auction load for non-tenant routes (login, forbidden). */
    skipAuctionLoad?: boolean;
  }
}

const STATUS_ORDER: Record<AuctionEventStatus, number> = {
  draft: 0,
  published: 1,
  open: 2,
  closed: 3,
};

function lifecycleSatisfied(
  current: AuctionEventStatus | null | undefined,
  required: AuctionEventStatus | readonly AuctionEventStatus[],
): boolean {
  if (current == null) return false;
  if (Array.isArray(required)) return required.includes(current);
  return STATUS_ORDER[current] >= STATUS_ORDER[required as AuctionEventStatus];
}

/**
 * Admin shell route guards — requireAdminAuth, requiredRole gating, and
 * lifecycleGate per A0.2 sidebar. fe-admin-route-guards (next ticket)
 * extends these with the full /forbidden redirect + RBAC mapping.
 */
export function rootGuards(router: Router): void {
  router.beforeEach(async (to) => {
    const session = useAdminSession();

    if (to.meta.requiresSupervisor) {
      if (session.jwt && isJwtExpired(session.jwt)) {
        session.clear();
      }
      if (!session.isSupervisor) {
        return { name: 'login', query: { next: to.fullPath } };
      }
      // Assumed into an org — returning to the lobby re-adopts the root
      // supervisor identity kept in the parent-domain root cookie.
      if (session.user && !session.restoreSupervisorRoot()) {
        return { name: 'login', query: { next: to.fullPath } };
      }
      // Supervisor surfaces render on the bare domain. The session travels
      // in the parent-domain cookie, so a plain origin hop suffices.
      if (getOrgSlug() !== '') {
        window.location.replace(bareAdminUrl(to.fullPath));
        return false;
      }
      return true;
    }

    if (to.meta.requiresAdminAuth) {
      if (session.jwt && isJwtExpired(session.jwt)) {
        session.clear();
      }
      if (!session.isAuthenticated) {
        return { name: 'login', query: { next: to.fullPath } };
      }
      if (session.isSupervisor && !session.orgId) {
        // A supervisor in the lobby following a link to an org subdomain
        // enters that org rather than landing back in the lobby.
        const linkedSlug = getOrgSlug();
        if (linkedSlug !== '') return enterLinkedOrg(linkedSlug, to);
        return '/supervisor';
      }
      // Auth confirmed, not a supervisor-without-org redirect: evaluate the
      // org's client feature flags before any page mounts. Idempotent and
      // error-swallowing — never blocks navigation.
      await session.loadFeatureFlags();
    }

    // Org routing on a shared session (parent-domain cookie):
    //  - bare-domain entry with an active org session → hop to the org
    //    subdomain, where the app actually runs. Supervisors are handled
    //    above — the /supervisor lobby is their picker.
    //  - an org session visiting a DIFFERENT org's subdomain → hop to its
    //    own subdomain (a supervisor switches orgs via the lobby).
    // Plain origin hops: the cookie is already visible everywhere, so the
    // old sso_jwt/auth-adopt transfer machinery is gone. replace() not
    // assign(): waypoints stay out of history to avoid Back-button loops.
    // A different org's subdomain is a followed link: enter that org when
    // the user has access (org-enter), instead of bouncing to the session's.
    if (to.meta.requiresAdminAuth && session.isAuthenticated) {
      const currentSlug = getOrgSlug();
      const onBareAdmin = currentSlug === '' && window.location.hostname.startsWith('admin.');
      let sessionSlug = session.user?.orgSlug ?? null;
      if (!sessionSlug && (onBareAdmin || currentSlug !== '')) {
        // orgSlug unknown (session adopted from claims, or minted without it):
        // resolve and remember it. Required on org subdomains too, or a
        // followed link to another org renders that URL with the session's
        // org data, because admin API calls are scoped by the JWT alone.
        try {
          const resp = await httpClient().get<AdminMe>('/admin/me');
          sessionSlug = resp.data.orgSlug ?? null;
          const { jwt, user } = session;
          if (jwt && user && isOrgSlug(sessionSlug)) {
            session.setSession(jwt, { ...user, orgSlug: sessionSlug });
          }
        } catch {
          // Couldn't resolve the org — render here rather than loop.
        }
      }
      // isOrgSlug, not a truthiness check: the slug arrives over the network and
      // lands in the redirect authority. A non-label value renders here instead.
      if (isOrgSlug(sessionSlug)) {
        if (currentSlug !== '' && currentSlug !== sessionSlug) {
          return enterLinkedOrg(currentSlug, to);
        }
        if (onBareAdmin) {
          window.location.replace(orgAdminUrl(sessionSlug, to.fullPath));
          return false;
        }
      }
    }

    if (to.meta.requiredRole && to.meta.requiredRole !== 'any') {
      if (session.role !== to.meta.requiredRole) {
        return { name: 'forbidden', query: { required: to.meta.requiredRole, current: session.role ?? 'none' } };
      }
    }

    if (to.params.id && typeof to.params.id === 'string' && !to.meta.skipAuctionLoad) {
      const auction = useCurrentAuction();
      try {
        await auction.loadById(to.params.id);
      } catch {
        if (to.name !== 'not-found') return { name: 'not-found' };
      }
      if (to.meta.lifecycle && !lifecycleSatisfied(auction.event?.status, to.meta.lifecycle)) {
        // Land on the status's home surface instead of dumping to the
        // workspace index — stale deep links keep the user in the auction
        // (2026-08-07 lifecycle design).
        const status = auction.event?.status;
        if (!status) return { name: 'auctions-index' };
        const landing = landingRouteNameFor(status);
        if (to.name === landing) return { name: 'auctions-index' }; // never self-redirect
        return { name: landing, params: { id: to.params.id } };
      }
    }

    return true;
  });
}

/**
 * Enter the org a followed link points at. Success reloads the page so every
 * org-scoped store and the realtime socket start over on the new session; the
 * reloaded guard then sees matching orgs and lets the route through.
 */
async function enterLinkedOrg(
  orgSlug: string,
  to: RouteLocationNormalized,
): Promise<false | RouteLocationRaw> {
  const outcome = await enterOrg(orgSlug);
  if (outcome === 'entered') {
    window.location.replace(to.fullPath);
    return false;
  }
  if (outcome === 'expired') {
    return { name: 'login', query: { next: to.fullPath, error: 'session_expired' } };
  }
  return {
    path: ORG_NO_ACCESS_PATH,
    query: { next: to.fullPath, ...(outcome === 'error' ? { reason: 'error' } : {}) },
  };
}
